import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import React, { FC, useEffect, useState } from 'react'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { LoadingContainer } from '@app/evaluation-adjustments/components/AdjustmentFactors.styles'
import {
  ResetAdjustmentButton,
  ResetAdjustmentButtonContent,
} from '@app/evaluation-adjustments/components/Adjustment.styles'
import * as S from './styled'

// The one field that is only a style: a button dressed as an input, for the
// rows that open a modal instead of taking typing.
export { FieldButton, FieldButtonIcon } from './styled'

// The check-or-alert pair the forms put beside a required field: filled in is
// a tick, still empty is a red alert. Only fields that validation can stop a
// form on carry one, so the icons and the error messages never disagree.
export const RequiredIndicator = ({ filled }: { filled: boolean }) =>
  filled ? (
    <S.CheckContainer>
      <FeatherIcon name="check" size={14} color="white" />
    </S.CheckContainer>
  ) : (
    <FeatherIcon name="alert-circle" size={18} color="#e53935" />
  )

interface FieldProps {
  label: string
  additionalText?: string
  indicator?: React.ReactNode
  inputWidth?: string
  hasError?: boolean
  errorText?: string
  isDefault?: boolean
  children: React.ReactNode
}

// The wrapper every field below is built from. Anything that is not a plain
// input - a date picker, a switch, a button that opens a modal - gets its row
// from here and keeps the rest of the form lined up with it.
export const Field: FC<FieldProps> = ({
  label,
  additionalText,
  indicator,
  inputWidth,
  hasError,
  errorText,
  isDefault,
  children,
}) => (
  <S.FieldRow inputWidth={inputWidth} hasError={hasError}>
    <div>
      <S.LabelGroup>
        <label>
          {/* The weight comes from the row, which is what keeps every label on
              both forms the same; only a field singled out as the default one
              is set heavier. */}
          <span style={isDefault ? { fontWeight: 600 } : undefined}>
            {label}
          </span>
          {additionalText && <span>{additionalText}</span>}
        </label>
        {indicator}
      </S.LabelGroup>
      <S.FieldControl>
        {children}
        {errorText && <S.ErrorText>{errorText}</S.ErrorText>}
      </S.FieldControl>
    </div>
  </S.FieldRow>
)

interface TextFieldProps {
  label: string
  defaultValue: string
  disabled?: boolean
  // A form that saves on its own commits on blur; one that keeps the value in
  // page state, next to a live preview of it, wants every keystroke.
  onBlur?: (value: string) => void
  onChange?: (value: string) => void
  additionalText?: string
  isInputWithButton?: boolean
  buttonTitle?: string
  isDefault?: boolean
  canReset?: boolean
  resetValue?: string
  uppercase?: boolean
  indicator?: React.ReactNode
  hasError?: boolean
  errorText?: string
  placeholder?: string
  type?: string
  min?: string
  max?: string
}

export const TextField: FC<TextFieldProps> = ({
  label,
  defaultValue,
  disabled,
  onBlur,
  onChange,
  additionalText,
  isInputWithButton,
  buttonTitle,
  isDefault,
  canReset,
  resetValue,
  uppercase,
  indicator,
  hasError,
  errorText,
  placeholder,
  type,
  min,
  max,
}) => {
  const transform = (v: string) => (uppercase ? v?.toUpperCase() ?? v : v)
  const [value, setValue] = useState(transform(defaultValue))

  useEffect(() => {
    setValue(transform(defaultValue))
  }, [defaultValue])

  const inputWithButtonStyles = {
    fontSize: 16,
    alignSelf: 'flex-start',
    width: '100%',
  }

  const defaultInputStyles = {
    width: 300,
  }

  const inputStyles = isInputWithButton
    ? inputWithButtonStyles
    : defaultInputStyles

  // Reset puts the original back without waiting for a blur that is never
  // coming, so the field, the page and whatever is saving all see it at once.
  const commit = async (next: string) => {
    const committed = transform(next)
    setValue(committed)
    onChange?.(committed)
    await onBlur?.(committed)
  }

  return (
    <Field
      label={label}
      additionalText={additionalText}
      indicator={indicator}
      hasError={hasError}
      errorText={errorText}
      isDefault={isDefault}
    >
      <S.ControlRow width={isInputWithButton ? '306px' : undefined}>
        {canReset && (
          <OriginalValue
            canEdit={true}
            originalValue={resetValue ?? ''}
            setValue={commit}
          />
        )}

        <input
          style={inputStyles}
          type={type}
          min={min}
          max={max}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            const next = transform(e.target.value)
            setValue(next)
            onChange?.(next)
          }}
          onBlur={() => onBlur?.(value)}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
        />
        {isInputWithButton && (
          <RoundedButton
            title={buttonTitle as string}
            onPress={() => onBlur?.(value)}
            style={{ marginLeft: 8 }}
          />
        )}
      </S.ControlRow>
    </Field>
  )
}

interface Option {
  value: string
  label: string
}

interface SelectFieldProps {
  label: string
  additionalText?: string
  defaultValue: string
  disabled?: boolean
  options: Option[]
  onChange: (value: string) => void
  inputWidth?: string
}

export const SelectField: FC<SelectFieldProps> = ({
  label,
  additionalText,
  defaultValue,
  disabled,
  options,
  onChange,
  inputWidth = '306px',
}) => {
  const [selectedValue, setSelectedValue] = useState(defaultValue)

  useEffect(() => {
    setSelectedValue(defaultValue)
  }, [defaultValue])

  return (
    <Field
      label={label}
      additionalText={additionalText}
      inputWidth={inputWidth}
    >
      <div style={{ display: 'flex' }}>
        <select
          disabled={disabled}
          value={selectedValue}
          onChange={(e) => {
            setSelectedValue(e.target.value)
            onChange(e.target.value)
          }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </Field>
  )
}

type OriginalValueProps = {
  canEdit?: boolean
  setValue: (value: string) => Promise<void>
  originalValue: string
}

export function OriginalValue({
  canEdit,
  setValue,
  originalValue,
}: OriginalValueProps) {
  const [resetting, setResetting] = useState(false)

  if (!canEdit) {
    return null
  }

  const onClick = async () => {
    setResetting(true)
    setTimeout(() => setResetting(false), 2000)
    await setValue(originalValue)
  }

  const valueWithResetIcon = (
    <>
      <div style={{ width: 'auto', height: '50%' }}>
        <span
          style={{
            fontSize: '14px',
          }}
        >{`(${originalValue})`}</span>
      </div>

      <div style={{ width: 'auto', height: '50%' }}>
        <FeatherIcon name="rotate-ccw" size={12} />
      </div>
    </>
  )

  const spinner = (
    <LoadingContainer>
      <FeatherIcon name="loader" size={16} />
    </LoadingContainer>
  )
  return (
    <>
      <ResetAdjustmentButton
        onClick={!resetting ? onClick : () => console.log('Invalid action')}
        style={{ width: 'auto', marginRight: 8 }}
      >
        {resetting ? (
          spinner
        ) : (
          <ResetAdjustmentButtonContent>
            {valueWithResetIcon}
          </ResetAdjustmentButtonContent>
        )}
      </ResetAdjustmentButton>
    </>
  )
}
